import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { CombinedState } from 'reducers';
import { getCore } from 'cvat-core-wrapper';
import {
    activateObject, changeFrameAsync, fetchAnnotationsAsync, removeObjectAsync, saveAnnotationsAsync,
    updateAnnotationsAsync,
} from 'actions/annotation-actions';
import { isReferenceJob } from 'utils/reference-job';
import { ReferencesProvider } from './references-context';
import { useTaskModes } from './use-task-modes';
import type { StateLike } from './reference-model';

export default function ReferencesProviderContainer({ children }: { children: React.ReactNode }): JSX.Element {
    const dispatch = useDispatch();
    const { tid } = useParams<{ tid?: string }>();
    const taskId = Number(tid);
    const job = useSelector((state: CombinedState) => state.annotation.job.instance);
    const frame = useSelector((state: CombinedState) => state.annotation.player.frame.number);
    const version = useSelector((state: CombinedState) => state.annotation.annotations.states);
    const activeClientID = useSelector((state: CombinedState) => state.annotation.annotations.activatedStateID);
    const modes = useTaskModes(Number.isInteger(taskId) ? taskId : 0);

    const collect = useCallback(async (): Promise<StateLike[]> => {
        const all: StateLike[] = [];
        // the whole job sits in memory after load: get() per frame reads it, no requests
        for (let number = job.startFrame; number <= job.stopFrame; number++) {
            all.push(...await job.annotations.get(number, false, []));
        }
        return all;
    }, [job]);
    const hasUnsavedChanges = useCallback(() => job.annotations.hasUnsavedChanges(), [job]);
    const save = useCallback(async () => {
        await dispatch(saveAnnotationsAsync() as any);
        if (job.annotations.hasUnsavedChanges()) throw new Error('Your changes could not be saved');
    }, [dispatch, job]);
    const reload = useCallback(async () => { await dispatch(fetchAnnotationsAsync(true) as any); }, [dispatch]);
    const update = useCallback(async (states: StateLike[]) => {
        await dispatch(updateAnnotationsAsync(states) as any);
    }, [dispatch]);
    const remove = useCallback(async (states: StateLike[]) => {
        for (const state of states) {
            // eslint-disable-next-line no-await-in-loop
            // the user confirmed in the dialog: a locked shape goes too, as in the Objects list
            await dispatch(removeObjectAsync(job, state, true) as any);
        }
        await save();
    }, [dispatch, job, save]);
    // the shape lives on another frame: activating before the frame loads would find nothing to activate
    const activate = useCallback((target: number, clientID: number) => {
        const moved = target !== frame ? dispatch(changeFrameAsync(target) as any) : Promise.resolve();
        Promise.resolve(moved).then(() => dispatch(activateObject(clientID, null, null) as any));
    }, [dispatch, frame]);

    if (!job || !isReferenceJob(job) || !Number.isInteger(taskId)) return children as JSX.Element;
    return (
        <ReferencesProvider
            jobId={job.id}
            taskId={taskId}
            currentFrame={frame}
            activeClientID={activeClientID}
            version={version}
            photos={modes?.photos ?? []}
            backendAPI={getCore().config.backendAPI}
            collect={collect}
            hasUnsavedChanges={hasUnsavedChanges}
            save={save}
            reload={reload}
            update={update}
            remove={remove}
            activate={activate}
        >
            {children}
        </ReferencesProvider>
    );
}
